import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import ExcelTable from 'components/ui/excel-table';
import {
  AllRowsData,
  ECellValueDisplayMode,
  ECellValueType,
  ExcelTableColDef,
  RowData,
} from 'components/ui/excel-table/types';
import { EditableCallbackParams } from 'ag-grid-community';
import {
  BinderScheduleItem,
  calculateBinderSchedule,
  EBinderScheduleKey,
  validateBinderSchedule,
} from 'components/features/binding/contract-detail/binder-schedule/form';
import { ECurrencies } from 'constants/enums';
import { ContractResponseDtoContractStatusEnum } from 'libs/pinnacle-sdk';

interface BinderScheduleSectionProps {
  originalCurrency?: string;
  layerPremium: number;
  setBinderScheduleData: (data: BinderScheduleItem[]) => void;
  binderScheduleData: BinderScheduleItem[];
  binderShareSigned: number;
  brokerCountry?: string;
  layerTotalDeductions?: string;
  binderPinCommission?: string;
  contractCurrencyRate: number;
  disabled?: boolean;
  setIsBinderScheduleValid: (valid: boolean) => void;
  currency: string;
  allowPremiumEdit?: boolean;
  contractStatus?: ContractResponseDtoContractStatusEnum;
}

const BinderScheduleSection: React.FC<BinderScheduleSectionProps> = ({
  originalCurrency,
  layerPremium,
  setBinderScheduleData,
  binderScheduleData,
  binderShareSigned,
  brokerCountry,
  layerTotalDeductions,
  binderPinCommission,
  contractCurrencyRate,
  disabled = false,
  setIsBinderScheduleValid,
  currency,
  allowPremiumEdit = false,
  contractStatus,
}) => {
  const { t } = useTranslation();

  const viewOnlyMode = useMemo(() => {
    if (disabled) return true;
    if (originalCurrency === ECurrencies.USD) return false;

    return currency !== originalCurrency;
  }, [currency, originalCurrency, disabled]);

  const onDataChange = useCallback(
    async (
      _: RowData<BinderScheduleItem>,
      rows: AllRowsData<BinderScheduleItem>
    ) => {
      const updatedSchedule = calculateBinderSchedule(
        rows.data,
        {
          premium: Number(layerPremium),
          binderShareSigned,
          brokerCountry,
          layerTotalDeductions,
          binderPinCommission,
        },
        t,
        allowPremiumEdit
      );
      setBinderScheduleData(updatedSchedule);
    },
    [
      binderShareSigned,
      layerPremium,
      t,
      setBinderScheduleData,
      brokerCountry,
      layerTotalDeductions,
      binderPinCommission,
    ]
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        headerName: '',
        field: 'label',
        flex: 1,
        cellStyle: {
          textAlign: 'center',
        },
        headerClass: 'ag-header-center',
        cellClass: (params) => {
          const item = params.data as BinderScheduleItem;
          return item.isBold ? 'font-bold' : '';
        },
        editable: false,
      },
      {
        headerName: `${t('contractDetail.rate')} %`,
        field: 'rate',
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        precision: (data: BinderScheduleItem) => {
          if (data.key === EBinderScheduleKey.PREMIUM) return 4;
          return 2;
        },
        width: 300,
        editable: (params: EditableCallbackParams) => {
          if (viewOnlyMode) return false;
          const item = params.data as BinderScheduleItem;
          return !!item.isEditable && !isNil(item.rate);
        },
        textAlign: 'center',
        headerClass: 'ag-header-center',
        flex: 1,
      },
      {
        headerName: t('contractDetail.fullAmount'),
        field: 'fullAmount',
        type: ECellValueType.NUMERIC_COLUMN,
        width: 300,
        cellStyle: (params) => {
          const item = params.data as BinderScheduleItem;
          return {
            textAlign: 'center',
            color: item.isNegative ? 'rgb(239, 68, 68)' : 'inherit',
          };
        },
        headerClass: 'ag-header-center',
        cellClass: (params) => {
          const item = params.data as BinderScheduleItem;
          return item.isBold ? 'font-bold' : '';
        },
        editable: false,
        flex: 1,
      },
      {
        headerName: t('contractDetail.amountOurShare'),
        field: 'amount',
        type: ECellValueType.NUMERIC_COLUMN,
        width: 300,
        cellStyle: (params) => {
          const item = params.data as BinderScheduleItem;
          return {
            textAlign: 'center',
            color: item.isNegative ? 'rgb(239, 68, 68)' : 'inherit',
          };
        },
        headerClass: 'ag-header-center',
        cellClass: (params) => {
          const item = params.data as BinderScheduleItem;
          return item.isBold ? 'font-bold' : '';
        },
        editable: (params: EditableCallbackParams) => {
          if (viewOnlyMode) return false;
          const item = params.data as BinderScheduleItem;
          return !!item.isEditable && isNil(item.rate);
        },
        flex: 1,
      },
    ],
    [t, viewOnlyMode]
  );

  const binderScheduleDataWithCurrencyRate = useMemo(() => {
    return binderScheduleData.map((item) => ({
      ...item,
      amount: !isNil(item.amount)
        ? item.amount * contractCurrencyRate
        : undefined,
      fullAmount: !isNil(item.fullAmount)
        ? item.fullAmount * contractCurrencyRate
        : undefined,
    }));
  }, [binderScheduleData, contractCurrencyRate]);

  const tableData = useMemo(() => {
    const appliedBinderScheduleData =
      currency === originalCurrency
        ? binderScheduleData
        : binderScheduleDataWithCurrencyRate;
    return appliedBinderScheduleData.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  }, [
    binderScheduleData,
    binderScheduleDataWithCurrencyRate,
    currency,
    originalCurrency,
  ]);

  const onErrorChange = useCallback(
    (isError: { strict: boolean; soft: boolean }) => {
      if (!isError.soft && !isError.strict) {
        setIsBinderScheduleValid(true);
      } else {
        setIsBinderScheduleValid(false);
      }
    },
    [setIsBinderScheduleValid]
  );

  return (
    <ExcelTable
      data={tableData}
      columnDefs={columnDefs}
      rowKeyColName="id"
      includeIdRow={false}
      tableContainerClassName="h-[33rem]"
      onDataChange={onDataChange}
      validateRow={validateBinderSchedule(contractStatus)}
      onErrorChange={onErrorChange}
    />
  );
};

export default BinderScheduleSection;
